/**
 * 발송 내역 탭 데이터 훅 — 이력 목록 / 상세 / 발송건별 수신자(PII 마스킹).
 *
 * be-notif Task#3 공표 계약(AdminNotificationHistoryController, base `/admin/v1/notification/send-histories`).
 * 목록 필터 = source/channel/status/from/to + pageable. 응답 `{items}` + meta. raw 엔드포인트(adminFetch).
 */
import { useQuery } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import type { NotificationChannel } from '../types/notificationMessage';
import type {
  SendHistoryDetail,
  SendHistoryItem,
  SendHistoryListFilter,
  SendHistoryListPage,
  SendHistoryRecipient,
  SendHistoryRecipientsPage,
  SendHistoryPurpose,
  SendHistorySource,
} from '../types/notificationHistory';

const BASE = '/admin/v1/notification/send-histories';

interface Envelope<T> {
  success: boolean;
  data?: T | null;
  error?: { code?: string; message?: string } | null;
}

export class SendHistoryApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  constructor(status: number, code: string | undefined, message: string) {
    super(message);
    this.name = 'SendHistoryApiError';
    this.status = status;
    this.code = code;
  }
}

const readEnvelope = async <T>(res: Response, fallback: string): Promise<T> => {
  let env: Envelope<T> | null = null;
  try {
    env = (await res.json()) as Envelope<T>;
  } catch {
    env = null;
  }
  if (!res.ok || !env || env.success !== true || env.data == null) {
    throw new SendHistoryApiError(res.status, env?.error?.code, env?.error?.message ?? fallback);
  }
  return env.data;
};

type Rec = Record<string, unknown>;
const str = (v: unknown): string | null => (typeof v === 'string' ? v : null);
const num = (v: unknown): number => (typeof v === 'number' ? v : 0);

const VALID_CHANNELS: ReadonlyArray<string> = ['EMAIL', 'SMS', 'KAKAO', 'KAKAO_BIZ'];
const asChannel = (v: unknown): NotificationChannel | null =>
  typeof v === 'string' && VALID_CHANNELS.includes(v) ? (v as NotificationChannel) : null;

const asSource = (v: unknown): SendHistorySource | null =>
  v === 'AUTO' || v === 'BULK' || v === 'SINGLE' ? v : null;

const asPurpose = (v: unknown): SendHistoryPurpose | null =>
  v === 'INFORMATIONAL' || v === 'ADVERTISING' ? v : null;

const normalizeItem = (raw: Rec): SendHistoryItem => ({
  historyId: String(raw.historyId ?? raw.id ?? ''),
  source: asSource(raw.source),
  sourceLabel: str(raw.sourceLabel),
  channel: asChannel(raw.channel),
  channelLabel: str(raw.channelLabel),
  templateCode: str(raw.templateCode),
  title: str(raw.title),
  status: str(raw.status),
  statusLabel: str(raw.statusLabel),
  recipientCount: num(raw.recipientCount),
  successCount: num(raw.successCount),
  failCount: num(raw.failCount),
  scheduledAt: str(raw.scheduledAt),
  sentAt: str(raw.sentAt),
  // notification #28 — 대체발송 연결(종전엔 여기서 빠져 수신자 목록의 "연결된 건" 링크가 실제로는 나오지 않았다).
  relatedHistoryId: str(raw.relatedHistoryId),
  purpose: asPurpose(raw.purpose),
  recipientLoginId: str(raw.recipientLoginId),
  recipientPhone: str(raw.recipientPhone),
  recipientEmail: str(raw.recipientEmail),
  bodyPreview: str(raw.bodyPreview),
});

const normalizeDetail = (raw: Rec): SendHistoryDetail => ({
  ...normalizeItem(raw),
  bodyPreview: str(raw.bodyPreview),
  requestedBy: str(raw.requestedBy),
  errorSummary: str(raw.errorSummary),
});

const normalizeRecipient = (raw: Rec): SendHistoryRecipient => ({
  recipientId: String(raw.recipientId ?? raw.id ?? ''),
  loginId: str(raw.loginId),
  maskedName: str(raw.maskedName),
  maskedContact: str(raw.maskedContact),
  status: str(raw.status),
  statusLabel: str(raw.statusLabel),
  errorMessage: str(raw.errorMessage),
  sentAt: str(raw.sentAt),
  deliveredAt: str(raw.deliveredAt),
});

const asArray = (data: unknown): Rec[] => {
  if (Array.isArray(data)) return data as Rec[];
  if (data && typeof data === 'object') {
    const rec = data as Rec;
    if (Array.isArray(rec.items)) return rec.items as Rec[];
    if (Array.isArray(rec.content)) return rec.content as Rec[];
  }
  return [];
};

const totalOf = (data: unknown, fallback: number): number => {
  if (data && typeof data === 'object') {
    const rec = data as Rec;
    if (typeof rec.total === 'number') return rec.total;
    if (typeof rec.totalElements === 'number') return rec.totalElements;
  }
  return fallback;
};

const buildQs = (filter: SendHistoryListFilter): string => {
  // BE 계약: source/channel/status/from/to(ISO) + pageable. q(검색어)는 BE 미지원.
  // QA-427 — 원문 연락처 요청(회원 목록 unmask 와 같은 방식, 권한은 BE 가 판단). BE 반영 전에는 무시된다.
  const qs = new URLSearchParams({ page: String(filter.page), size: String(filter.size), sort: 'createdAt,desc', unmask: 'true' });
  if (filter.source) qs.set('source', filter.source);
  // QA-427 — 목적·수신자·회원 검색(BE 전달 문서). BE 반영 전에는 무시된다.
  if (filter.purpose) qs.set('purpose', filter.purpose);
  if (filter.recipient) qs.set('recipient', filter.recipient);
  if (filter.userId) qs.set('userId', filter.userId);
  if (filter.channel) qs.set('channel', filter.channel);
  if (filter.status) qs.set('status', filter.status);
  if (filter.startDate) qs.set('from', new Date(`${filter.startDate}T00:00:00`).toISOString());
  if (filter.endDate) qs.set('to', new Date(`${filter.endDate}T23:59:59`).toISOString());
  return qs.toString();
};

export const sendHistoryListQueryKey = (filter: SendHistoryListFilter) =>
  ['admin', 'notification', 'send-histories', 'list', filter] as const;

export const useSendHistoryListQuery = (filter: SendHistoryListFilter) =>
  useQuery<SendHistoryListPage>({
    queryKey: sendHistoryListQueryKey(filter),
    queryFn: async () => {
      const res = await adminFetch(`${BASE}?${buildQs(filter)}`);
      const data = await readEnvelope<unknown>(res, '발송 내역을 불러오지 못했습니다');
      const items = asArray(data).map(normalizeItem);
      return { items, total: totalOf(data, items.length), page: filter.page, size: filter.size };
    },
    staleTime: 30_000,
  });

export const sendHistoryDetailQueryKey = (id: string) =>
  ['admin', 'notification', 'send-histories', 'detail', id] as const;

export const useSendHistoryDetailQuery = (id: string | null) =>
  useQuery<SendHistoryDetail>({
    queryKey: sendHistoryDetailQueryKey(id ?? ''),
    queryFn: async () => {
      const res = await adminFetch(`${BASE}/${id ?? ''}`);
      return normalizeDetail(await readEnvelope<Rec>(res, '발송 상세를 불러오지 못했습니다'));
    },
    enabled: Boolean(id),
  });

export const sendHistoryRecipientsQueryKey = (id: string, page: number) =>
  ['admin', 'notification', 'send-histories', 'recipients', id, page] as const;

export const useSendHistoryRecipientsQuery = (id: string | null, page: number, size = 20) =>
  useQuery<SendHistoryRecipientsPage>({
    queryKey: sendHistoryRecipientsQueryKey(id ?? '', page),
    queryFn: async () => {
      const qs = new URLSearchParams({ page: String(page), size: String(size), unmask: 'true' });
      const res = await adminFetch(`${BASE}/${id ?? ''}/recipients?${qs.toString()}`);
      const data = await readEnvelope<unknown>(res, '수신자 목록을 불러오지 못했습니다');
      const items = asArray(data).map(normalizeRecipient);
      return { items, total: totalOf(data, items.length), page, size };
    },
    enabled: Boolean(id),
    staleTime: 15_000,
  });
